<template>
  <span
    class="inline-flex items-center space-x-1.5 truncate font-medium ring-1 ring-inset"
    :class="[sizeClasses, roundedClasses, colorClasses, customClass]"
  >
    <svg
      v-if="dot"
      class="size-1.5 shrink-0"
      :class="`fill-${props.color}-500`"
      viewBox="0 0 6 6"
      aria-hidden="true"
    >
      <circle cx="3" cy="3" r="3" :class="dot ? `fill-${color}-500` : ''" />
    </svg>
    <component
      v-if="prefixIcon"
      :is="prefixIcon"
      class="size-4 text-gray-400"
      aria-hidden="true"
    />
    <span v-if="title" class="truncate">{{ title }}</span>
    <slot />
  </span>
</template>

<script setup lang="ts">
import type { Icon, CustomColor } from '@/types';

type BadgeSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl';

const props = defineProps({
  color: {
    type: String as PropType<CustomColor>,
    default: 'indigo' as CustomColor,
  },
  size: {
    type: String as PropType<BadgeSize>,
    default: 'xs' as BadgeSize,
  },
  title: {
    type: String,
    default: '',
  },
  pill: {
    type: Boolean,
    default: false,
  },
  dot: {
    type: Boolean,
    default: false,
  },
  customClass: {
    type: String,
    default: '',
  },
  prefixIcon: {
    type: Function as PropType<Icon>,
    default: null,
  },
});

const colorClasses = computed(() => {
  return `bg-${props.color}-50 text-${props.color}-600 ring-${props.color}-600/20`;
});

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'xs':
      return 'px-2 py-1 text-xs';
    case 'sm':
      return 'px-2 py-1 text-sm';
    case 'base':
      return 'px-2.5 py-1.5 text-sm';
    case 'lg':
      return 'px-3 py-2 text-sm';
    case 'xl':
      return 'px-3.5 py-2.5 text-sm';
    default:
      return '';
  }
});

const roundedClasses = computed(() => {
  if (props.pill) {
    return 'rounded-full';
  }
  const size = props.size === 'xs' || props.size === 'sm' ? '-md' : '';
  return `rounded${size}`;
});
</script>
